/**
 * ButtonConfigPanel Component
 *
 * Configuration panel for Button widgets.
 * Allows editing displayName, className (textarea), and functionName (textarea).
 * Uses design system components for consistent styling.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { ButtonConfigPanelProps } from '../Widgets/types';
import { BUTTON_DEFAULT_CONFIG } from '../../config/widgetDefaults';
import TextField from 'Components/DesignSystem/TextField/TextField';
import FieldTitle from 'Components/DesignSystem/FieldTitle/FieldTitle';
import Typography from 'Components/DesignSystem/Typography/Typography';
import Divider from 'Components/DesignSystem/Divider/Divider';
import Textarea from 'Components/DesignSystem/Textarea/Textarea';
import { useDebouncedInput } from '../../hooks/useDebouncedInput';

const DEBOUNCE_DELAY = 500;

export const ButtonConfigPanel = ({
  config,
  onChange,
}: ButtonConfigPanelProps) => {
  // Local state for immediate UI updates
  const [localConfig, setLocalConfig] = useState({
    ...BUTTON_DEFAULT_CONFIG,
    ...config,
  });

  // Sync with external config changes
  useEffect(() => {
    setLocalConfig({ ...BUTTON_DEFAULT_CONFIG, ...config });
  }, [config]);

  // Create debounced change handlers
  const handleConfigChange = useCallback(
    (key: keyof typeof localConfig, value: string) => {
      const newConfig = { ...localConfig, [key]: value };
      setLocalConfig(newConfig);
      onChange(newConfig);
    },
    [localConfig, onChange]
  );

  // Debounced inputs using the hook
  const displayNameInput = useDebouncedInput(
    localConfig.displayName,
    (value) => handleConfigChange('displayName', value),
    DEBOUNCE_DELAY
  );

  const classNameInput = useDebouncedInput(
    localConfig.className,
    (value) => handleConfigChange('className', value),
    DEBOUNCE_DELAY
  );

  const functionNameInput = useDebouncedInput(
    localConfig.functionName,
    (value) => handleConfigChange('functionName', value),
    DEBOUNCE_DELAY
  );

  return (
    <div className="tw-space-y-6">
      <div className="tw-p-3">
        {/* Widget Header with 3-dot menu */}
        <div className="tw-flex tw-items-center tw-justify-between tw-pb-3">
          <Typography variant="title-header" className="tw-text-ink">
            Widget
          </Typography>
          <button data-testid="configpanel-buttonconfigpanel-button-1" aria-label="More options" className="tw-text-ink-secondary hover:tw-text-ink">
            <svg className="tw-w-5 tw-h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
            </svg>
          </button>
        </div>
        <Divider className="tw-mb-4" />

        {/* Display Name Field (Required) */}
        <div className="tw-mt-3">
          <TextField
            label="Display Name"
            required
            value={displayNameInput.localValue}
            onChange={displayNameInput.handleChange}
            placeholder="Button"
          />
        </div>

        {/* Action Type Section Header */}
        <div className="tw-mt-4">
          <FieldTitle label="Action Type" required />
        </div>

        {/* Class Field with Tooltip */}
        <div className="tw-mt-3">
          <FieldTitle
            label="Class"
            infoMessage="The specified class(es) will be added to the HTML button element's class attribute. You can use this for styling (CSS) or for attaching JavaScript event listeners to handle button functionality."
            className="tw-mb-2"
          />
          <Textarea
            label=""
            rows={2}
            value={classNameInput.localValue}
            onChange={(value) => classNameInput.handleChange(value)}
            placeholder="Enter or paste class"
            data-testid="configpanel-buttonconfigpanel-textarea-2"
          />
        </div>

        {/* Function Field with Tooltip */}
        <div className="tw-mt-3">
          <FieldTitle
            label="Function"
            infoMessage="Enter the name of a JavaScript function that exists as a window variable (e.g., window.myFunction). This function will be called when the button is clicked. Define the function in your website's code before using the UI builder."
            className="tw-mb-2"
          />
          <Textarea
            label=""
            rows={2}
            value={functionNameInput.localValue}
            onChange={(value) => functionNameInput.handleChange(value)}
            placeholder="Enter or paste function"
            data-testid="configpanel-buttonconfigpanel-textarea-3"
          />
        </div>
      </div>
    </div>
  );
};

export default ButtonConfigPanel;
